import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  LinearProgress,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ExpandMore,
  Add,
  AutoAwesome,
  Delete,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { rolesAPI } from '../../../roles/services/roles';
import RolePermissionsManager from '../../../roles/components/RolePermissionsManager';
import { STANDARD_ROLES } from '../../data/standardRoles';
import { useStandardRoles } from '../../hooks/useStandardRoles';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { getApiErrorCode, isDuplicateRoleError } from '../../utils/apiErrors';
import LoadErrorAlert from '../LoadErrorAlert';

export default function Step1RolesPermissions({ onRefresh }) {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showStandardDialog, setShowStandardDialog] = useState(false);
  const [selectedPresets, setSelectedPresets] = useState([]);
  const [form, setForm] = useState({ name: '', description: '' });
  const [formError, setFormError] = useState({});
  const [saving, setSaving] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { createStandardRoles, loadPermissions, permissions, creating, progress } = useStandardRoles();
  const { showSuccess, showError, showInfo } = useSnackbar();

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await rolesAPI.getDepartmentRoles();
      setRoles(res.data?.roles || []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const existingRoleNames = useMemo(
    () => new Set(roles.map(r => r.name.trim().toLowerCase())),
    [roles]
  );

  const presetName = useCallback(id => t(`onboarding:standard_roles.${id}.name`), [t]);

  const presetExists = useCallback(
    id => existingRoleNames.has(presetName(id).trim().toLowerCase()),
    [existingRoleNames, presetName]
  );

  const handleAddRole = async () => {
    const errors = {};
    if (!form.name.trim()) errors.name = t('onboarding:step1.errors.name_required');
    if (!form.description.trim()) errors.description = t('onboarding:step1.errors.description_required');
    if (Object.keys(errors).length) { setFormError(errors); return; }

    setSaving(true);
    try {
      await rolesAPI.createRole({ name: form.name.trim(), description: form.description.trim() });
      setForm({ name: '', description: '' });
      setFormError({});
      setShowAddForm(false);
      showSuccess(t('onboarding:step1.success.created'));
      await fetchRoles();
      onRefresh();
    } catch (e) {
      showError(isDuplicateRoleError(e)
        ? t('onboarding:step1.errors.duplicate_name')
        : t('onboarding:step1.errors.create_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    setDeleting(true);
    try {
      // Staff assignments were confirmed in the dialog, so force removes them
      await rolesAPI.deleteRole(roleToDelete.role_id, { force: roleToDelete.staff_count > 0 });
      showSuccess(t('onboarding:step1.success.deleted'));
      setRoleToDelete(null);
      await fetchRoles();
      onRefresh();
    } catch (e) {
      showError(getApiErrorCode(e) === 'ROLE__IN_USE'
        ? t('onboarding:step1.errors.in_use')
        : t('onboarding:step1.errors.delete_failed'));
    } finally {
      setDeleting(false);
    }
  };

  const openStandardDialog = () => {
    setSelectedPresets(STANDARD_ROLES.filter(p => !presetExists(p.id)).map(p => p.id));
    setShowStandardDialog(true);
    // Only used for permission display names; creation retries on its own
    loadPermissions().catch(() => {});
  };

  const handleCreateStandardRoles = async () => {
    if (!selectedPresets.length) return;
    const { created, skipped, failed } = await createStandardRoles(
      selectedPresets,
      roles.map(r => r.name)
    );
    setShowStandardDialog(false);
    setSelectedPresets([]);

    const messages = [];
    if (created.length) {
      messages.push(t('onboarding:step1.success.standard_created', { count: created.length }));
    }
    if (skipped.length) {
      messages.push(t('onboarding:step1.success.standard_skipped', { count: skipped.length, names: skipped.join(', ') }));
    }
    if (failed.length) {
      messages.push(t('onboarding:step1.errors.standard_failed', { names: failed.join(', ') }));
      showError(messages.join(' '));
    } else if (created.length) {
      showSuccess(messages.join(' '));
    } else {
      showInfo(messages.join(' '));
    }

    await fetchRoles();
    onRefresh();
  };

  const togglePreset = (id) =>
    setSelectedPresets(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t('onboarding:step1.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('onboarding:step1.subtitle')}
      </Typography>

      {/* Roles section */}
      <Stack direction="row" spacing={1} mb={2}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setShowAddForm(v => !v)}
          size="small"
        >
          {t('onboarding:step1.add_role')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<AutoAwesome />}
          onClick={openStandardDialog}
          disabled={loading || loadError}
          size="small"
        >
          {t('onboarding:step1.add_standard_roles')}
        </Button>
      </Stack>

      {showAddForm && (
        <Box
          sx={{
            p: 2, mb: 2, border: 1, borderColor: 'divider',
            borderRadius: 1, bgcolor: 'background.paper',
          }}
        >
          <Stack spacing={2}>
            <TextField
              label={t('onboarding:step1.form_name')}
              size="small"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              error={!!formError.name}
              helperText={formError.name}
              autoFocus
            />
            <TextField
              label={t('onboarding:step1.form_description')}
              size="small"
              multiline
              rows={2}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              error={!!formError.description}
              helperText={formError.description}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleAddRole}
                disabled={saving}
              >
                {saving ? <CircularProgress size={16} /> : t('common:buttons.save')}
              </Button>
              <Button size="small" onClick={() => { setShowAddForm(false); setFormError({}); }}>
                {t('common:buttons.cancel')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : loadError ? (
        <LoadErrorAlert onRetry={fetchRoles} />
      ) : roles.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('onboarding:step1.no_roles')}
        </Alert>
      ) : (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            {t('onboarding:step1.permissions_section')}
          </Typography>
          <Stack spacing={1}>
            {roles.map(role => (
              <Accordion key={role.role_id} variant="outlined">
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <Typography fontWeight={600}>{role.name}</Typography>
                    <Chip
                      label={t('onboarding:step1.permissions_count', { count: role.permissions_count })}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={t('common:buttons.delete')}
                    onClick={e => { e.stopPropagation(); setRoleToDelete(role); }}
                    sx={{ mr: 1 }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <RolePermissionsManager
                    role={role}
                    onUpdate={() => { fetchRoles(); onRefresh(); }}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </>
      )}

      {/* Delete role confirmation */}
      <Dialog
        open={!!roleToDelete}
        onClose={() => !deleting && setRoleToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('onboarding:step1.delete_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('onboarding:step1.delete_confirm', { name: roleToDelete?.name })}
          </DialogContentText>
          {roleToDelete?.staff_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('onboarding:step1.delete_staff_warning', { count: roleToDelete.staff_count })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleToDelete(null)} disabled={deleting}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteRole}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {t('common:buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Standard roles dialog */}
      <Dialog
        open={showStandardDialog}
        onClose={() => !creating && setShowStandardDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('onboarding:step1.dialog_title')}</DialogTitle>
        <DialogContent>
          {creating && progress ? (
            <Box mb={2}>
              <Typography variant="body2" mb={1}>
                {progress.current > 0
                  ? t('onboarding:step1.creating_progress', progress)
                  : t('onboarding:step1.creating')}
              </Typography>
              <LinearProgress
                variant={progress.current > 0 ? 'determinate' : 'indeterminate'}
                value={progress.current > 0 ? ((progress.current - 1) / progress.total) * 100 : undefined}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('onboarding:step1.dialog_subtitle')}
            </Typography>
          )}
          <Stack spacing={1}>
            {STANDARD_ROLES.map(preset => {
              const exists = presetExists(preset.id);
              const selected = selectedPresets.includes(preset.id);
              const disabled = exists || creating;
              return (
                <Box
                  key={preset.id}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: selected ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: exists ? 0.6 : 1,
                    bgcolor: selected ? 'action.selected' : undefined,
                  }}
                  onClick={() => !disabled && togglePreset(preset.id)}
                >
                  <FormControlLabel
                    disabled={disabled}
                    control={
                      <Checkbox
                        checked={selected}
                        onChange={() => togglePreset(preset.id)}
                        onClick={e => e.stopPropagation()}
                      />
                    }
                    label={
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography fontWeight={600}>{presetName(preset.id)}</Typography>
                          {exists && (
                            <Chip label={t('onboarding:step1.already_exists')} size="small" />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {t(`onboarding:standard_roles.${preset.id}.description`)}
                        </Typography>
                        <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
                          {preset.permissions.map(p => (
                            <Chip key={p} label={permissions[p]?.name || p} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                  />
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStandardDialog(false)} disabled={creating}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateStandardRoles}
            disabled={!selectedPresets.length || creating}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {creating
              ? t('onboarding:step1.creating')
              : t('onboarding:step1.create_roles', { count: selectedPresets.length })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
